import { useParamsWithMatchPath, useRequest } from '@/hooks';
import { getLimitsForPlan, PlanLimits } from '@/config/planLimits';

const PLAN_NAME_MAP: Record<PlanType, string> = {
  Free: '무료',
  Pro: '프로',
  Enterprise: '엔터프라이즈',
  Class: '클래스',
  Teacher: '티처',
  Realworld: '리얼월드'
};

/**
 * 현재 채널의 요금제 제한사항을 가져오는 Hook
 *
 * @example
 * ```tsx
 * const { limits, plan, canUse, hasReached } = usePlanLimits();
 *
 * if (canUse('gps')) {
 *   // GPS 기능 활성화
 * }
 *
 * if (hasReached('projects', projectCount)) {
 *   // 업그레이드 안내
 * }
 * ```
 */
export function usePlanLimits() {
  const { appId } = useParamsWithMatchPath();
  const { data: app, isValidating } = useRequest<RealWorldApp>(appId ? `/apps/${appId}` : null);

  const plan: PlanType = app?.planType ?? 'Free';
  const limits = getLimitsForPlan(plan);

  return {
    /** 현재 플랜 */
    plan,

    /** 전체 제한사항 객체 */
    limits,

    /** 로딩 중 여부 */
    isLoading: isValidating,

    /**
     * 특정 기능 사용 가능 여부
     * @example canUse('gps')
     */
    canUse: (feature: keyof PlanLimits['features']) => {
      return limits.features[feature];
    },

    /**
     * 제한 도달 여부
     * @example hasReached('projects', 5)
     */
    hasReached: (limitType: 'projects' | 'items', currentCount: number) => {
      const maxKey = limitType === 'projects' ? 'maxProjects' : 'maxItems';
      const max = limits[maxKey];

      if (max === null) {
        return false; // 무제한
      }

      return currentCount > max;
    },

    /**
     * 남은 개수
     * @returns null이면 무제한
     */
    getRemaining: (limitType: 'projects' | 'items', currentCount: number) => {
      const maxKey = limitType === 'projects' ? 'maxProjects' : 'maxItems';
      const max = limits[maxKey];

      if (max === null) {
        return null; // 무제한
      }

      return Math.max(0, max - currentCount);
    },
    getPlanName: (plan: PlanType) => {
      return PLAN_NAME_MAP[plan];
    }
  };
}
